import { Box, Button, Skeleton } from '@mui/material';
import { Income, IIncomeBase } from 'common/entities';
import { IFormState, IValidationState } from 'common/types';
import RightDrawer from 'components/BaseLayout/RightDrawer';
import {
  LabeledCheckbox,
  TextField
} from 'components/controls/formControls';
import { useAppDispatch, useAppSelector } from 'core/hooks/hooks';
import { useEntityEditForm } from 'core/hooks/useEntityEditForm';
import {
  NotificationContext,
  NotificationContextType
} from 'core/providers/NotificationContextProvider';
import { addIncome, getIncome, updateIncome } from 'core/store/actions/incomes.actions';
import { resetCurrentIncome } from 'core/store/reducers/incomes.reducer';
import { incomesSelector } from 'core/store/selectors/incomes.selector';
import { FC, useContext, useEffect } from 'react';

export interface EditIncomeFormProps {
  incomeId?: string;
  isOpen: boolean;
  closePanel: () => void;
}

const initialState: IFormState = {
  name: { value: '' },
  repeatable: { value: false }
};

const validationSchema: IValidationState = {
  name: {
    required: true
  }
};

const EditIncomeForm: FC<EditIncomeFormProps> = ({
  incomeId,
  isOpen,
  closePanel
}) => {
  const { showNotification } = useContext(
    NotificationContext
  ) as NotificationContextType;
  const { lastAction, error, isLoading, currentIncome } =
    useAppSelector(incomesSelector);
  const dispatch = useAppDispatch();
  const { formState, updateEntityProperty, handleSubmit } =
    useEntityEditForm<Income>(
      incomeId,
      initialState,
      currentIncome,
      isOpen,
      createFormState,
      addIncomeAction,
      updateIncomeAction,
      getIncome,
      validationSchema
    );

  useEffect(() => {
    switch (lastAction) {
      case addIncome.fulfilled.type:
        closePanel();
        showNotification({
          message: `Income "${formState.name.value}" has been added`
        });
        break;
      case addIncome.rejected.type:
        showNotification({ isError: true, message: `Error: ${error}` });
        break;
      case getIncome.rejected.type:
        showNotification({ isError: true, message: `Error: ${error}` });
        closePanel();
        break;
      case updateIncome.fulfilled.type:
        closePanel();
        showNotification({
          message: `Income "${formState.name.value}" has been updated`
        });
        break;
      case updateIncome.rejected.type:
        showNotification({ isError: true, message: `Error: ${error}` });
        break;
    }
  }, [lastAction]);

  useEffect(() => {
    if (!isOpen) {
      dispatch(resetCurrentIncome());
    }
  }, [isOpen]);

  const handleInputChange = event => {
    const eventValue =
      event.target.type === 'number' && !isNaN(event.target.valueAsNumber)
        ? event.target.valueAsNumber
        : event.target.value;
    updateEntityProperty(event.target.name, eventValue);
  };

  const handleCheckboxChange = event => {
    updateEntityProperty(event.target.name, event.target.checked);
  };

  function createFormState(income: Income): IFormState {
    return {
      name: { value: income.name },
      repeatable: { value: income.repeatable },
    };
  }

  function createIncome(): IIncomeBase {
    return {
      name: formState.name.value,
      repeatable: formState.repeatable.value,
    };
  }

  function addIncomeAction() {
    return addIncome(createIncome());
  }

  function updateIncomeAction() {
    if (!incomeId) return;
    return updateIncome({
      id: incomeId,
      income: createIncome()
    });
  }

  const styles = {
    width: '100%'
  };

  return (
    <RightDrawer
      isOpen={isOpen}
      closePanel={closePanel}
      title={incomeId ? 'Edit income' : 'Add income'}
    >
      {!isLoading && (
        <Box component="form" sx={styles} onSubmit={handleSubmit}>
          <TextField
            id="name"
            name="name"
            value={formState.name.value}
            handleChange={handleInputChange}
            title="Name"
            error={formState.name.error}
          />
          <LabeledCheckbox
              id="repeatable"
              name="repeatable"
              label="Repeatable"
              handleChange={handleCheckboxChange}
              isChecked={!!formState.repeatable?.value}
            />
          <Box
            sx={{ display: 'flex', justifyContent: 'right', marginTop: '30px' }}
          >
            <Button type="submit">
              {incomeId ? 'Update income' : 'Add income'}
            </Button>
          </Box>
        </Box>
      )}
      {isLoading && (
        <Skeleton variant="rectangular" height="80vh" width="600px" />
      )}
    </RightDrawer>
  );
};

export default EditIncomeForm;
